import { ApolloError } from "@apollo/client";
import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import { trackEvent } from "apps/web/src/reporting/reporting";
import React, { useState } from "react";
import {
  GptRecipeFragment,
  useGetPossibleGptRecipeFromImageMutation,
  useGetPossibleGptRecipeFromUrlMutation,
} from "../../../../types";
import { GPTLoadingContent } from "../GPTLoadingContent";
import { TranscribeWithGPTContent } from "./TranscribeWithGPTContent";
import { TranscribeError } from "./utils";

interface TranscribeWithGPTDialogProps {
  open: boolean;
  onClose: () => void;
  onTranscribeComplete: (gptRecipe: GptRecipeFragment, closeModal: () => void) => void;
  onError: (error: any) => void;
  transcriberError: TranscribeError | null;
  setTranscriberError: (error: TranscribeError | null) => void;
}

const isExactlyOneTruthy = (a: boolean, b: boolean) => a !== b;

export const TranscribeWithGPTDialog = ({
  open,
  onClose,
  onTranscribeComplete,
  onError,
  transcriberError,
  setTranscriberError,
}: TranscribeWithGPTDialogProps) => {
  const { isGetRecipeFromUrlEnabled } = useNutritionFlags();
  const [presignedGetImageUrl, setPresignedGetImageUrl] = useState("");
  const [recipeUrl, setRecipeUrl] = useState("");
  const [invalidUrl, setInvalidUrl] = useState(false);

  const [transcribeImage, { loading: transcribingImage }] = useGetPossibleGptRecipeFromImageMutation({
    onCompleted: data => {
      onTranscribeComplete(data.getPossibleGPTRecipeFromImage, onClose);
    },
  });
  const [transcribeUrl, { loading: transcribingUrl }] = useGetPossibleGptRecipeFromUrlMutation({
    onCompleted: data => {
      onTranscribeComplete(data.getPossibleGPTRecipeFromUrl, onClose);
    },
  });

  const loading = transcribingImage || transcribingUrl;

  const handleClose = () => {
    trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Cancel Transcription", {});
    if (loading) {
      return;
    }
    onClose();
  };

  const handleTranscribeError = (e: any, inputType: string) => {
    let message = `There was an issue transcribing your ${inputType}.`;
    if (e instanceof ApolloError) {
      if (e.message.includes("invalid image")) {
        // it's possible this is caused by a broken upload link - there should
        // be a log with the full error output from OpenAI
        message = message + " Please try again with a different image. If the issue persists, please reach out to customer support.";
      } else if (e.message.toLowerCase().includes("unreadable image")) {
        message = message + " The image is too blurry to transcribe.";
      } else if (e.message.toLowerCase().includes("no recipe found")) {
        message = message + ` Recipe content could not be found in the provided ${inputType}.`;
      } else if (e.message.toLowerCase().includes("cannot be translated")) {
        message = message + " The transcriber cannot translate non-English recipes at this time.";
      }
    }
    setPresignedGetImageUrl("");
    setRecipeUrl("");
    setTranscriberError(
      new TranscribeError({
        name: "GPT_ERROR",
        message: message,
        cause: e,
      })
    );
  };

  const handleTranscribe = async () => {
    if (presignedGetImageUrl) {
      await transcribeImage({ variables: { input: { presignedGetImageUrl: presignedGetImageUrl } } }).catch(e =>
        handleTranscribeError(e, "image")
      );
      trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Transcribe Image", {});
    } else if (isGetRecipeFromUrlEnabled && recipeUrl) {
      await transcribeUrl({ variables: { input: { url: recipeUrl } } }).catch(e => handleTranscribeError(e, "URL"));
      trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Transcribe URL", { url: recipeUrl });
    }
  };

  const hasImage = Boolean(presignedGetImageUrl);
  const hasUrl = isGetRecipeFromUrlEnabled && Boolean(recipeUrl);
  const disableTranscribe =
    loading ||
    (isGetRecipeFromUrlEnabled ? !isExactlyOneTruthy(hasImage, hasUrl) || (hasUrl && invalidUrl) : !hasImage);

  const onChangeRecipeUrl = (url: string) => {
    setRecipeUrl(url);
    if (!url) {
      setInvalidUrl(false);
      return;
    }
    try {
      new URL(url);
    } catch {
      setInvalidUrl(true);
      return;
    }
    const urlRegex = /^(((http|https):\/\/|)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,6}(:[0-9]{1,5})?(\/.*)?)$/;
    setInvalidUrl(!urlRegex.test(url));
  };

  const dialogTitle = isGetRecipeFromUrlEnabled ? "AI Image/URL Transcriber" : "AI Image Transcriber";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle title={dialogTitle} onClose={handleClose} />
      <DialogContent sx={{ pt: loading ? 6 : 1, pb: loading ? 6 : 12 }}>
        {loading ? (
          <GPTLoadingContent actionText="Transcribing" />
        ) : (
          <div>
            <TranscribeWithGPTContent
              presignedGetImageUrl={presignedGetImageUrl}
              setPresignedGetImageUrl={setPresignedGetImageUrl}
              recipeUrl={recipeUrl}
              onChangeRecipeUrl={onChangeRecipeUrl}
              invalidUrl={invalidUrl}
              showUrlUpload={isGetRecipeFromUrlEnabled}
              transcriberError={transcriberError}
              setTranscriberError={setTranscriberError}
              onError={onError}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <span>
            <Button onClick={handleTranscribe} disabled={disableTranscribe}>
              Continue
            </Button>
          </span>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
